import { useCallback, useLayoutEffect, useRef, useState } from "react";

import { observer } from "mobx-react-lite";
import { useWindowSize } from "usehooks-ts";

import { CancelLines } from "@/components/expression/utils/decorators/Cancel";
import { getBoundaryClassNames } from "@/components/expression/utils/getBoundaryClassNames";
import * as Node from "@/components/expression/utils/layout";
import { useNodeStyle } from "@/components/expression/utils/NodeStyleProvider";
import { WRAPPER_NODE_CLASS_NAME } from "@/models/CursorModel";
import { SCREEN_SM } from "@/style/breakpoints";
import { CONTENT_WRAPPER_WIDTH } from "@/style/sizes";
import { twMerge } from "@/utils/twMerge";
import { useResizeObserver } from "@/utils/useResizeObserver";

import { InlineNode } from "./InlineNode";

import type { ExpressionComponent } from "./types";

type Props = React.ComponentProps<"div"> & {
  enableIndentation?: boolean;
  isFocused?: boolean;
};

export const WrapperNode: ExpressionComponent<"wrapper", Props> = observer(function WrapperNode({
  node,
  className,
  enableIndentation,
  isFocused,
  ...rest
}) {
  const { renderContext, availableWidth: contextAvailableWidth } = useNodeStyle();
  const { width: windowWidth } = useWindowSize();
  const availableWidth = getAvailableWidth(windowWidth, contextAvailableWidth);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [enableRows, setEnableRows] = useState(false);
  const [needsScroll, setNeedsScroll] = useState(false);

  const { doc, inlineNode } = node;
  const chatContext = renderContext === "chat-input" || renderContext === "chat-messages";

  const updateLayout = useCallback(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    const { scrollWidth, clientWidth } = el;

    if (!enableRows && scrollWidth > availableWidth) {
      setEnableRows(true);
      return;
    }

    const paddingX = 3; // 1.5px * 2
    const overflow = scrollWidth - clientWidth - paddingX;

    setNeedsScroll(overflow > 2);
  }, [availableWidth, enableRows]);

  useLayoutEffect(() => {
    updateLayout();
  }, [updateLayout]);

  useResizeObserver(containerRef, updateLayout);

  let renderBoundaries = false;
  let variant: "focused" | "computed" | undefined = undefined;
  if (isFocused && (renderContext === "editor" || renderContext === "chat-input")) {
    renderBoundaries = true;
    variant = "focused";
  }
  const inlineFocused = doc.cursor.node === inlineNode;
  const isHighlighted = doc.editor.highlightedWrapperIds.has(node.id);
  const { phantomHintResult, previewAtCursor } = doc.editor.hints;
  const trailingHintShown = !!phantomHintResult && !previewAtCursor;

  return (
    <Node.Container
      node={node}
      dimension="tight"
      data-computed={String(node.computed)}
      className={twMerge(
        WRAPPER_NODE_CLASS_NAME,
        "flex p-0 rounded-5 print-color-economy",
        // Clears the slot for the long-press gesture.
        "[-webkit-touch-callout:none]",
        "[&:has([data-inline-node-row-id]:nth-child(2))]:w-full",
        '[[data-change-request-part="before"]_[data-math]_&]:text-red-950 dark:[[data-change-request-part="before"]_[data-math]_&]:text-red-100',

        "transition-[padding] duration-100",

        isHighlighted &&
          "bg-green-400/30 dark:bg-green-400/40 ring-4 ring-green-400/30 dark:ring-green-400/40 transition-colors duration-200",

        renderBoundaries &&
          twMerge(
            "px-[2px] sm:px-0",
            inlineFocused && doc.cursor.index === 0 && "pl-[4px] sm:px-0",
            inlineFocused && doc.cursor.index === inlineNode?.nodes.length && "pr-[4px] sm:px-0",
          ),
        className,

        getBoundaryClassNames(
          // A trailing hint draws the right boundary itself; one rendered at the caret does not.
          renderBoundaries && (trailingHintShown ? "left" : true),
          variant,
        ),
      )}
      disableHover
      {...rest}
    >
      <div
        ref={containerRef}
        className={twMerge(
          "w-fit [&:has([data-inline-node-row-id]:nth-child(2))]:w-full align-baseline px-[1.5px]",

          "max-w-[calc(100vw-90px)]",
          needsScroll && "overflow-x-auto",

          chatContext && twMerge("max-w-full no-scrollbar", needsScroll && "overflow-x-auto"),

          renderContext === "chat-input" && "sm:max-w-[calc(100vw-60px)]",

          renderContext === "chat-messages" && "max-w-[calc(100vw-120px)]",
        )}
      >
        {node.inlineNode && (
          <InlineNode
            node={node.inlineNode}
            availableWidth={availableWidth}
            enableRows={enableRows}
            enableIndentation={enableIndentation}
          />
        )}
      </div>
      <CancelLines
        type="xcancel"
        className={
          'change-request-math-xcancel pointer-events-none z-20 hidden [[data-change-request-part="before"]_[data-math]_&]:block'
        }
      />
    </Node.Container>
  );
});

function getAvailableWidth(windowWidth: number, contextAvailableWidth?: number): number {
  const isMobile = windowWidth < SCREEN_SM;

  if (contextAvailableWidth) {
    return contextAvailableWidth;
  }

  if (isMobile) {
    return windowWidth - 80;
  }

  return CONTENT_WRAPPER_WIDTH - 20;
}
